import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Container,
  CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';

interface FallbackErrorProps {
  error?: Error;
  componentName?: string;
  resetError?: () => void;
  retryCount?: number;
  maxRetries?: number;
  isRetrying?: boolean;
}

export const FallbackError: React.FC<FallbackErrorProps> = ({
  error,
  componentName,
  resetError,
  retryCount = 0,
  maxRetries = 3,
  isRetrying = false
}) => {
  const handleRetry = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const canRetry = retryCount < maxRetries;

  return (
    <Container maxWidth="sm">
      <Paper
        sx={{
          p: 3,
          mt: 4,
          textAlign: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <BugReportIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom color="error">
          {componentName ? `Error in ${componentName}` : 'Something went wrong'}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {error?.message || 'An unexpected error occurred.'}
        </Typography>
        {retryCount > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Retry attempt {retryCount} of {maxRetries}
          </Typography>
        )}
        <Box sx={{ mt: 3 }}>
          {isRetrying ? (
            <CircularProgress size={24} />
          ) : (
            canRetry && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRetry}
                startIcon={<RefreshIcon />}
                disabled={isRetrying}
              >
                {retryCount === 0 ? 'Try Again' : 'Retry'}
              </Button>
            )
          )}
          {!canRetry && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Maximum retry attempts reached. Please refresh the page or contact support.
            </Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};
